<template>
  <div id="organisation-selector">
    <h5 v-if="withTitle">
      {{ $tc('signup.form.usergroup.title') }}
    </h5>
    <hr
      v-if="withTitle"
      class="divider"
    />
    <div v-if="!showCreationForm">
      <label for="organisationSelector-organisation">
        {{ subtitle }}
      </label>
      <div class="infos">
        {{ $tc('signup.form.usergroup.info') }}
      </div>
      <Multiselect
        id="organisationSelector-organisation"
        data-test="organisationSelector-organisation"
        v-model="organisationSelected"
        style="margin-top: 0.5em;"
        class="search-usergroups"
        :options="suggestions.local"
        :optionsLimit="10"
        :allow-empty="false"
        track-by="id"
        label="display_name"
        :resetAfter="false"
        selectLabel=""
        selectedLabel=""
        deselectLabel=""
        :searchable="true"
        :placeholder="$tc('signup.form.usergroup.placeholder')"
        :showNoResults="true"
        :loading="loading"
        :clearOnSelect="false"
        :preserveSearch="false"
        @search-change="search"
      >
        <template slot="clear">
          <div
            v-if="organisationSelected"
            class="multiselect__clear"
            data-test="organisationSelector-removeOrganisation"
            @click.prevent.stop="clearOrganisation()"
          >
            <b-icon-x font-scale="2"/>
          </div>
        </template>
        <span slot="noResult">
          {{ $t('multiselect.noResult') }}
        </span>
        <span slot="noOptions">
          {{ $t('signup.form.usergroup.noOptions') }}
        </span>
      </Multiselect>
      <p v-if="withCreation">
        {{ $t('signup.form.usergroup.help.text') }}
        <a
          data-test="organisationSelector-showCreationFormTrue"
          @click="updateShowCreationForm(true)"
        >
          {{ $t('signup.form.usergroup.help.link') }}
        </a>
      </p>
    </div>
    <OrganisationCreation
      v-if="showCreationForm && withCreation"
      data-test="organisationSelector-showCreationFormFalse"
      :name="displayName"
      :siret="registrationNumber"
      @update="updateOrganisationToCreate"
      @cancel="updateShowCreationForm(false)"
    />
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex';
import { debounce } from 'lodash';
import Bloodhound from 'corejs-typeahead/dist/bloodhound';

import OrganisationCreation from '@/components/OrganisationCreation.vue';

export default {
  name: 'OrganisationSelector',
  components: {
    OrganisationCreation
  },
  props: {
    withTitle: {
      type: Boolean,
      default: true
    },
    subtitle: {
      type: String,
      default: ''
    },
    withCreation: {
      type: Boolean,
      default: true
    },
    showCreationForm: {
      type: Boolean,
      default: false
    },
    displayName: {
      type: String,
      default: null
    },
    registrationNumber: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      text: null,
      suggestions: {
        local: []
      },
      organisation: null,
      role: null,
      organisationSelected: null,
      organisationToCreate: {
        codename: null,
        display_name: null,
        description: null,
        organisation_type: null,
        registration_number: null,
        phone_number: null,
        website_url: null,
        postal_address: null,
        isValid: false,
      },
      organisationThumbnail: null,
      organisationSpheres: [],
    };
  },
  computed: {
    ...mapState('user', [
      'userData'
    ]),
    ...mapState('usergroups', [
      'organisationsList',
    ]),
  },
  watch: {
    text: debounce(async function(newValue) {
      this.SET_USERGROUPS_SEARCH_QUERY(newValue);
      this.loading = true;
      await this.SEARCH_USERGROUPS(['organisation']);
      this.setSuggestionsEngine();
      this.loading = false;
    }, 100),

    organisationsList: {
      deep: true,
      handler() {
        this.setSuggestionsEngine();
      }
    },

    userData: {
      deep: true,
      handler() {
        this.setSuggestionsEngine();
      }
    },
    organisationToCreate: {
      deep: true,
      handler(newValue) {
        if (newValue) {
          this.$emit('create', {
            organisation: newValue,
            thumbnail: this.organisationThumbnail,
            spheres: this.organisationSpheres
          });
        }
      }
    },
    organisationThumbnail(newValue) {
      if (newValue && this.organisationSelected && this.organisationSelected.id) {
        this.$emit('create', {
          organisation: this.organisationsList.find(el => el.id === this.organisationSelected.id),
          thumbnail: newValue,
          spheres: this.organisationSpheres
        });
      }
    },
    organisationSpheres: {
      deep: true,
      handler(newValue) {
        if (newValue && this.organisationSelected && this.organisationSelected.id) {
          this.$emit('create', {
            organisation: this.organisationsList.find(el => el.id === this.organisationSelected.id),
            thumbnail: this.organisationThumbnail,
            spheres: newValue
          });
        }
      }
    },
    organisationSelected(newValue) {
      if (newValue && newValue.id) {
        this.$emit('select', this.organisationsList.find(el => el.id === newValue.id));
      }
    },
  },

  async created() {
    if (this.organisationsList.length === 0) {
      try {
        this.loading = true;
        await this.GET_USERGROUPS_LIST(['organisation']);
        this.setSuggestionsEngine();
        this.loading = false;
      } catch {
        this.loading = false;
      }
    } else {
      this.setSuggestionsEngine();
    }
  },

  methods: {
    ...mapMutations('usergroups', [
      'SET_USERGROUPS_SEARCH_QUERY'
    ]),
    ...mapActions('usergroups', [
      'GET_USERGROUPS_LIST',
      'SEARCH_USERGROUPS'
    ]),

    setSuggestionsEngine() {
      const SuggestionsEngine = Bloodhound.noConflict();
      let local = [];
      this.organisationsList
        .filter((el) => {
          if (this.userData) {
            return this.userData.usergroup_roles.findIndex(role => role.usergroup.id === el.id) === -1;
          } else {
            return true;
          }
        })
        .map(function(item) {
          local.push({
            id: item.id,
            display_name: item.display_name
          });
        });
      this.suggestions = new SuggestionsEngine({
        local: local,
        initialize: true,
        datumTokenizer: Bloodhound.tokenizers.obj.nonword('title'),
        queryTokenizer: Bloodhound.tokenizers.nonword
      });
    },

    search(text) {
      this.text = text;
    },

    clear() {
      this.text = null;
      this.organisationSelected = null;
    },

    updateOrganisationToCreate(e) {
      this.organisationToCreate = e.form;
      this.organisationSpheres = e.spheres;
      this.organisationThumbnail = e.thumbnail;
      
      if (e.form.display_name && e.form.organisation_type) {
        this.organisationToCreate.isValid = true;
      }
      else {
        this.organisationToCreate.isValid = false;
      }
    },

    updateShowCreationForm(value) {
      this.$emit('update:showCreationForm', value);
    },

    clearOrganisation() {
      this.organisationSelected = null;
      this.$emit('select', null);
    },
  },
};
</script>

<style lang="less" scoped>
#organisation-selector {
  margin-top: 2rem;
  width: 100%;
}
label {
  font-size: 0.87rem;
  font-weight: bold;
}
p {
  padding-top: 0.2rem;
  font-size: 0.9rem;
  a {
    color: #105182 !important;
  }
  a:hover {
    cursor: pointer;
    text-decoration: underline !important;
  }
}
.infos {
  font-size: 0.8em;
  font-style: italic;
  margin-right: 1em;
}
</style>
